<template>
  <div class="sections-holder">
    <FaqSection>
      <span slot="heading">
        What is ordinary least squares (OLS)?
      </span>
      <div slot="content">
        <p>We use the ordinary least squares (OLS) method for regression analysis.</p>
        <p>‘Least squares’ represents the minimum squares error. This is the most common way to estimate the linear regression equation. You already know that a lower error means a better explanatory power of the regression model. So, this method aims to find the line that minimizes the sum of the squared errors (SSE).</p>
        <p>Let’s take a closer look at the graph below.</p>
        <p>We can typically find many lines that fit the data. The OLS, however, determines the one with the smallest error. On the graph, it’s the one closest to all points simultaneously.</p>
        <img class="img-dependent-independent" src="../../../../assets/images/calculators/linear-regression/linear-regression-24.webp" alt="Dependant, independent variables graph.">
        <p>And we use the following expression to calculate it.</p>
        <p class="formulas-overflow">
          \[min\ \sum_{i=1}^{n}\varepsilon_1^2\]
        </p>
        <p class="formulas-overflow">
          \[S(b)=\sum_{i=1}^{n}{(y_i-x_i^Tb)}^2={(y-Xb)}^T(y-Xb)\]
        </p>
        <p class="last-formula">
          S(b) is the OLS estimator of <span class="inline-formulas">\[\beta\]</span> for a simple linear regression.
        </p>
        <p>But how do we interpret the formula?</p>
        <p>
          This minimization problem uses calculus and <nuxt-link to="/tutorials/python-tutorials/linear-algebra-data-science/" target="_blank">
            linear algebra
          </nuxt-link> to determine the slope and intercept of the regression line. After you crunch the numbers, you’ll find the intercept is <span class="inline-formulas">\[b_0\]</span>, and the slope is <span class="inline-formulas">\[b_1\]</span>. Knowing the coefficients, you can quickly obtain the regression equation.
        </p>
        <p>We can minimize the squared sum of errors on paper, but this is almost impossible with datasets comprising thousands of values. Nowadays, professionals might let the software do the math, and regression analysis will be ready quickly.</p>
        <p>Statisticians typically prefer Excel, SPSS, SAS, and Stata for calculations. In contrast, data analysts and data scientists favor programming languages like R and Python because they offer limitless capabilities and unmatched speed.</p>
        <p>Our simple linear regression calculator also uses the OLS method. In addition to the results and calculations, it generates the relevant Python and R codes with your data.</p>
        <p>Of course, we use other methods for determining the regression line in different contexts. Common approaches include Generalized least squares, Maximum likelihood estimation, Bayesian regression, the Kernel regression, and the Gaussian process regression.</p>
        <p>Still, the least squares method remains the all-time favorite for many. It’s simple yet powerful enough for most linear problems.</p>
      </div>
    </FaqSection>
    <hr>
    <FaqSection>
      <span slot="heading">
        What are the degrees of freedom (DF)?
      </span>
      <div slot="content">
        <p>The degrees of freedom (DF) indicate the number of free values to vary. In the context of linear regression, you can think of it as the number of independent pieces of information you use to estimate the regression line. (Remember that DF is NOT the same as the number of observations in the sample.)</p>
        <p>Now, where does ‘freedom’ come from? This component shows how many values are ‘free to vary’ in a dataset.</p>
        <p>Suppose I ask you to choose three random numbers whose average must be 5. Anything that averages 5 will do. So, you can pick 4,5,6 or 3,5,7, or even 10, 1, and 4.</p>
        <p>But here’s the catch—once you pick the first two numbers, the third one is fixed. You’re not ‘free’ to choose the third number. Only the first two can vary. You may go for 1 and 6 or 2 and 4, but once you have made that decision, you must find that one number that gives you the desired mean of 5 when combined with the other two. So, we say that the degrees of freedom for the three numbers are three minus one, which gives us two. If we choose 5 numbers instead, the DF would be five minus one, or 4.</p>
      </div>
    </FaqSection>
    <hr>
    <FaqSection>
      <span slot="heading">
        Correlation vs Regression
      </span>
      <div slot="content">
        <p>The correlation vs regression analysis difference can be captured by one simple sentence: Correlation does not imply causation.</p>
        <p>Of course, there are other differences.</p>
        <p>For starters, correlation measures the degree of relatedness between two variables. It doesn’t capture causality. Conversely, regression reveals how one variable affects or changes another.</p>
        <p>In addition, the correlation between x and y is the same as between y and x. You can easily see this in the formula (provided below), which is symmetrical. By contrast, regressions of y on x and x on y yield different results. The following example better illustrates this. If there’s a strong positive correlation between carrying an umbrella and rainy weather, we could assume that having an umbrella causes rain or vice versa.</p>
        <p>But this correlation does not imply a causal relationship. Carrying an umbrella is typically a response to the observation or expectation of rainy weather. Rainy weather itself is the cause for people to bring umbrellas, not the other way around.</p>
        <p>Finally, the two methods have very different graphical representations. Linear regression analysis uses the line of best fit, which is constructed to minimize the distance between the line and the data points. In contrast, correlation provides a single numerical value that quantifies the strength and direction of the relationship between variables. Therefore, it is graphically represented as a single point.</p>
        <p>Linear regression analysis is represented with a line of best fit that goes through the data points and minimizes the distance between them, while correlation is a single point.</p>
        <p class="img-heading">
          Comparison between correlation and regression
        </p>
        <img src="../../../../assets/images/calculators/linear-regression/linear-regression-25.webp" alt="Comparison between correlation and regression table.">
      </div>
    </FaqSection>
  </div>
</template>

<script>
import FaqSection from './Section.vue';

export default {
  components: {
    FaqSection,
  },
};
</script>

<style lang="scss" scoped>
.sections-holder {
  hr {
    margin: var(--spacing-8) 0 var(--spacing-24);
    border: 1px solid rgba(0, 12, 31, 0.12);
  }

  .faq-holder {
    .content {
      .img-heading {
        text-align: center;
        font-size: 26px;
        font-weight: 600;
        margin-top: var(--spacing-40);
        color: var(--accent-shade-3);

        & + img {
          margin-bottom: 0;
        }
      }

      .img-dependent-independent {
        margin-left: var(--spacing-60);
      }

      .last-formula {
        text-align: center;
        color: var(--main-dark);
      }
    }
  }
}

@include bp($bp-laptop) {
  .sections-holder {
    .faq-holder {
      .content {
        .img-heading {
          font-size: 22px;
        }

        .img-dependent-independent {
          margin-left: var(--spacing-50);
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .sections-holder {
    .faq-holder {
      .content {
        .img-heading {
          font-size: 18px;
        }

        .img-dependent-independent {
          margin-left: 0;
        }
      }
    }
  }
}
</style>
